import { apiClient } from './client';
import {
  CompanyInventoryItem,
  CompanyInventoryMaster,
  CompanyInventoryMasterPayload,
  CompanyInventoryMovement,
  CompanyInventoryPayload,
  InventoryItemKind,
} from '../types/companyInventory';
import { DEFAULT_PAGE_SIZE, PageResponse } from '../types/page';

export async function fetchCompanyInventory(params?: {
  kind?: InventoryItemKind | 'ALL' | string;
  keyword?: string;
}): Promise<CompanyInventoryItem[]> {
  const { data } = await apiClient.get<CompanyInventoryItem[]>('/company-inventory', {
    params: {
      ...(params?.kind && params.kind !== 'ALL' ? { kind: params.kind } : {}),
      ...(params?.keyword?.trim() ? { q: params.keyword.trim() } : {}),
    },
  });
  return data;
}

export async function fetchCompanyInventoryMasters(params?: {
  kind?: InventoryItemKind | 'ALL' | string;
  keyword?: string;
}): Promise<CompanyInventoryMaster[]> {
  const { data } = await apiClient.get<CompanyInventoryMaster[]>('/company-inventory/masters', {
    params: {
      ...(params?.kind && params.kind !== 'ALL' ? { kind: params.kind } : {}),
      ...(params?.keyword?.trim() ? { q: params.keyword.trim() } : {}),
    },
  });
  return data;
}

export async function fetchCompanyInventoryMaster(masterId: number): Promise<CompanyInventoryMaster> {
  const { data } = await apiClient.get<CompanyInventoryMaster>(
    `/company-inventory/masters/${masterId}`
  );
  return data;
}

export async function createCompanyInventoryMaster(
  payload: CompanyInventoryMasterPayload
): Promise<CompanyInventoryMaster> {
  const { data } = await apiClient.post<CompanyInventoryMaster>(
    '/company-inventory/masters',
    payload
  );
  return data;
}

export async function updateCompanyInventoryMaster(
  masterId: number,
  payload: Partial<CompanyInventoryMasterPayload>
): Promise<CompanyInventoryMaster> {
  const { data } = await apiClient.patch<CompanyInventoryMaster>(
    `/company-inventory/masters/${masterId}`,
    payload
  );
  return data;
}

export async function deleteCompanyInventoryMaster(masterId: number): Promise<void> {
  await apiClient.delete(`/company-inventory/masters/${masterId}`);
}

export async function addCompanyInventorySpec(
  masterId: number,
  payload: Partial<CompanyInventoryPayload>
): Promise<CompanyInventoryItem> {
  const { data } = await apiClient.post<CompanyInventoryItem>(
    `/company-inventory/masters/${masterId}/items`,
    payload
  );
  return data;
}

export async function fetchCompanyInventoryItem(
  inventoryId: number
): Promise<CompanyInventoryItem> {
  const { data } = await apiClient.get<CompanyInventoryItem>(
    `/company-inventory/${inventoryId}`
  );
  return data;
}

export async function createCompanyInventory(
  payload: CompanyInventoryPayload
): Promise<CompanyInventoryItem> {
  const { data } = await apiClient.post<CompanyInventoryItem>('/company-inventory', payload);
  return data;
}

export async function updateCompanyInventory(
  inventoryId: number,
  payload: Partial<CompanyInventoryPayload>
): Promise<CompanyInventoryItem> {
  const { data } = await apiClient.patch<CompanyInventoryItem>(
    `/company-inventory/${inventoryId}`,
    payload
  );
  return data;
}

export async function deleteCompanyInventory(inventoryId: number): Promise<void> {
  await apiClient.delete(`/company-inventory/${inventoryId}`);
}

export async function fetchCompanyInventoryMovements(
  inventoryId: number,
  params?: { page?: number; size?: number }
): Promise<PageResponse<CompanyInventoryMovement>> {
  const { data } = await apiClient.get<PageResponse<CompanyInventoryMovement>>(
    `/company-inventory/${inventoryId}/movements`,
    {
      params: {
        page: params?.page ?? 0,
        size: params?.size ?? DEFAULT_PAGE_SIZE,
      },
    }
  );
  return data;
}
